import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatbot } from '../hooks/useChatbot';
import { Send, Bot, User, Loader } from 'lucide-react';
import {
  StyledChatContainer,
  StyledHeader,
  StyledBotIcon,
  StyledTitle,
  StyledSubtitle,
  StyledChatArea,
  StyledMessagesContainer,
  StyledMessage,
  StyledMessageIcon,
  StyledMessageBubble,
  StyledMessageText,
  StyledSuggestionsContainer,
  StyledSuggestionChip,
  StyledInputContainer,
  StyledInput,
  StyledSendButton,
  StyledWelcomeMessage,
} from './ChatPage.styles';

const ChatPage: React.FC = () => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { messages, sendMessage, isLoading } = useChatbot();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue.trim();

    setInputValue('');
    await sendMessage(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
  };

  const suggestions = [
    t('chatbot.quickActions.overview'),
    t('chatbot.quickActions.savings'),
    t('chatbot.quickActions.expenses'),
    t('chatbot.quickActions.goals'),
  ];

  return (
    <StyledChatContainer>
      <StyledHeader>
        <StyledBotIcon>
          <Bot size={24} />
        </StyledBotIcon>
        <div>
          <StyledTitle>{t('chatbot.title')}</StyledTitle>
          <StyledSubtitle>Ask me anything about your finances</StyledSubtitle>
        </div>
      </StyledHeader>

      <StyledChatArea>
        <StyledMessagesContainer>
          {messages.length === 0 ? (
            <StyledWelcomeMessage>
              <p>👋 Hi! I'm your financial assistant.</p>
              <p>Ask me about your spending, savings, or any financial questions!</p>
            </StyledWelcomeMessage>
          ) : (
            messages.map((message, index) => (
              <StyledMessage key={index} $isUser={message.type === 'user'}>
                <StyledMessageIcon $isUser={message.type === 'user'}>
                  {message.type === 'user' ? <User size={14} /> : <Bot size={14} />}
                </StyledMessageIcon>
                <StyledMessageBubble $isUser={message.type === 'user'}>
                  <StyledMessageText>{message.message}</StyledMessageText>
                </StyledMessageBubble>
              </StyledMessage>
            ))
          )}

          {isLoading && (
            <StyledMessage $isUser={false}>
              <StyledMessageIcon $isUser={false}>
                <Loader size={14} />
              </StyledMessageIcon>
              <StyledMessageBubble $isUser={false}>
                <StyledMessageText>Thinking...</StyledMessageText>
              </StyledMessageBubble>
            </StyledMessage>
          )}

          <div ref={messagesEndRef} />
        </StyledMessagesContainer>

        {messages.length === 0 && (
          <StyledSuggestionsContainer>
            {suggestions.map((suggestion, index) => (
              <StyledSuggestionChip key={index} onClick={() => handleSuggestionClick(suggestion)}>
                {suggestion}
              </StyledSuggestionChip>
            ))}
          </StyledSuggestionsContainer>
        )}

        <StyledInputContainer>
          <StyledInput
            type='text'
            placeholder={t('chatbot.placeholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <StyledSendButton onClick={handleSendMessage} disabled={!inputValue.trim() || isLoading}>
            <Send size={14} />
          </StyledSendButton>
        </StyledInputContainer>
      </StyledChatArea>
    </StyledChatContainer>
  );
};

export default ChatPage;
